import { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Alert } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  CustomerServiceOutlined,
  BarChartOutlined,
  SettingOutlined,
  FlagOutlined,
  AuditOutlined,
  HeartOutlined,
  SafetyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MailOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useLogout } from '../hooks/useAuth';
import { ADMIN_ROLE_LABELS } from '../lib/constants';

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

const menuItems = [
  { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/users', icon: <UserOutlined />, label: 'Users' },
  { key: '/organizations', icon: <TeamOutlined />, label: 'Organizations' },
  { key: '/subscriptions', icon: <CreditCardOutlined />, label: 'Subscriptions' },
  { key: '/invoices', icon: <FileTextOutlined />, label: 'Invoices' },
  {
    key: 'support',
    icon: <CustomerServiceOutlined />,
    label: 'Support',
    children: [
      { key: '/support/tickets', label: 'Tickets' },
      { key: '/support/email-templates', icon: <MailOutlined />, label: 'Email Templates' },
    ],
  },
  { key: '/analytics', icon: <BarChartOutlined />, label: 'Analytics' },
  {
    key: 'system',
    icon: <SettingOutlined />,
    label: 'System',
    children: [
      { key: '/system/admin-users', icon: <CrownOutlined />, label: 'Admin Users' },
      { key: '/system/feature-flags', icon: <FlagOutlined />, label: 'Feature Flags' },
      { key: '/system/audit-logs', icon: <AuditOutlined />, label: 'Audit Logs' },
      { key: '/system/health', icon: <HeartOutlined />, label: 'System Health' },
    ],
  },
  { key: '/settings/security', icon: <SafetyOutlined />, label: 'Security' },
];

export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const adminUser = useAuthStore((s) => s.adminUser);
  const logout = useLogout();

  const userMenuItems = [
    { key: 'role', label: ADMIN_ROLE_LABELS[adminUser?.admin_role ?? ''] || adminUser?.admin_role, disabled: true },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={240}>
        <div style={{ height: 48, margin: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text strong style={{ color: '#fff', fontSize: collapsed ? 16 : 18 }}>
            {collapsed ? 'IA' : 'Invex Admin'}
          </Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={['support', 'system']}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: '0 24px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0' }}>
          <span onClick={() => setCollapsed(!collapsed)} style={{ cursor: 'pointer', fontSize: 18 }}>
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </span>
          <Dropdown menu={{ items: userMenuItems, onClick: ({ key }) => { if (key === 'logout') logout.mutate(); } }}>
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} size="small" />
              <Text>{adminUser?.full_name || adminUser?.email}</Text>
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ margin: 24 }}>
          {adminUser && !adminUser.admin_2fa_enabled && (
            <Alert
              type="warning"
              showIcon
              icon={<SafetyOutlined />}
              message="Two-factor authentication is not enabled"
              description={
                <span>
                  Secure your admin account by enabling 2FA.{' '}
                  {adminUser.admin_2fa_setup_deadline && (
                    <>Required by {new Date(adminUser.admin_2fa_setup_deadline).toLocaleDateString()}.{' '}</>
                  )}
                  <Link to="/settings/security">Set up now</Link>
                </span>
              }
              style={{ marginBottom: 16 }}
              banner
            />
          )}
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
